import { FC, ReactNode, useEffect, useState } from "react";
import { useBoolean } from "usehooks-ts";

import { serverUpsertPageAccessData } from "api/api-server-actions";
import { SUBMISSION_ID_QUERY_PARAM } from "components/constants";
import {
  getLateSubmissionDueDate,
  isInLateSubmissionWindow,
  isLateSubmissionDueDateIndefinite,
  isPastSubmissionDueDate,
} from "components/materials/page/helpers";
import { PageAccessDataType, PageType } from "components/server-types";
import { monthDayAtTime } from "i18n/helpers";
import { t } from "i18n/i18n";
import { IconText } from "mds/components/IconText";
import { PillButton } from "mds/components/PillButton";
import { Tooltip } from "mds/components/Tooltip";
import {
  CheckIcon,
  CircleCrossIcon,
  CircleSuccessIcon,
  LongFormPencilIcon,
  RepeatIcon,
} from "mds/icons";
import { cn } from "mds/utils/cn";
import { storeApi, useAppDispatch, useAppSelector } from "store/index";
import {
  selectCurrentSectionUserId,
  selectPageAccessDataForSectionUser,
  selectPageGroupForReadyUser,
  selectPageGroupsWithUsersByPageId,
  selectSelectedPageTabVariant,
  selectUserSubmissionsByPageId,
} from "store/selectors";
import { viewStateActions } from "store/slices/view";
import { useListSelector } from "store/store-hooks";
import { isGroupPage, isIndividualPage, isStaticPage } from "utils/page-utils";
import { useQueryParamsManager } from "utils/routing/hooks/use-query-params-manager";

interface StudentSubmitButtonProps {
  selectedPage: PageType;
}

export const StudentSubmitButton: FC<StudentSubmitButtonProps> = ({ selectedPage }) => {
  const dispatch = useAppDispatch();
  const { deleteQueryParams } = useQueryParamsManager();
  const sectionUserId = useAppSelector(selectCurrentSectionUserId);
  const tabVariant = useAppSelector(selectSelectedPageTabVariant);
  const group = useAppSelector((s) => selectPageGroupForReadyUser(s, selectedPage?.id));
  const isStaticContent = isStaticPage(selectedPage);
  const isGrouped = isGroupPage(selectedPage);
  const isIndividual = isIndividualPage(selectedPage);
  const pageAccessData = useAppSelector((s) =>
    selectPageAccessDataForSectionUser(s, selectedPage?.id, sectionUserId),
  );
  const hasDueDate = Boolean(selectedPage.due_at);
  const isPastDueDate = !isStaticContent && isPastSubmissionDueDate(selectedPage);
  const isWithinLateSubmissionPeriod = isInLateSubmissionWindow(selectedPage);
  const isDone = Boolean(pageAccessData?.completed_at);
  const showEditButton = isDone || tabVariant === "my_submission";
  const isDisabled = isGrouped && !group;

  if (!selectedPage) {
    return null;
  }

  const toggleDoneStatus = () => {
    // If we are on the Submission tab, switch to the Workspace tab ("my_draft")
    if (tabVariant === "my_submission") {
      deleteQueryParams([SUBMISSION_ID_QUERY_PARAM]);
      dispatch(viewStateActions.setSelectedPageTabVariant("my_draft"));

      // If the page is not done, return without updating the page access data
      // (because we want the page to remain in edit mode)
      if (!isDone) {
        return;
      }
    }

    const dataProps: Partial<PageAccessDataType> = isDone
      ? { completed_at: null, completed_by_id: null }
      : { completed_at: new Date().toISOString(), completed_by_id: sectionUserId };

    if (pageAccessData) {
      storeApi.page_access_data.partial_update({
        id: pageAccessData.id,
        ...dataProps,
      });
    } else {
      if (isGrouped) {
        dataProps.page_group_id = group?.id;
      } else if (isIndividual || isStaticContent) {
        // Static pages are treated as individual pages for the purpose of marking pages complete.
        dataProps.section_user_id = sectionUserId;
      }

      serverUpsertPageAccessData({
        page_id: selectedPage.id,
        section_id: selectedPage.section_id,
        ...dataProps,
      });
    }
  };

  if (isStaticContent) {
    return (
      <SubmitButtonForStaticPage
        page={selectedPage}
        showEditButton={showEditButton}
        toggleDoneStatus={toggleDoneStatus}
      />
    );
  }

  // If the due date has passed and late submissions are not allowed,
  // the submit button behaves the same as for a page with no due date.
  if (!hasDueDate || (isPastDueDate && !isWithinLateSubmissionPeriod)) {
    return (
      <SubmitButtonForPageWithNoDueDate
        disabled={isDisabled}
        disabledTooltip={t("page_view.group_mark_done_missing_group")}
        isGrouped={isGrouped}
        page={selectedPage}
        showEditButton={showEditButton}
        toggleDoneStatus={toggleDoneStatus}
      />
    );
  }

  if (isPastDueDate && isWithinLateSubmissionPeriod) {
    return (
      <SubmitButtonForPageWithPastDueDate
        page={selectedPage}
        pageAccessData={pageAccessData}
        showEditButton={showEditButton}
        toggleDoneStatus={toggleDoneStatus}
      />
    );
  }

  return (
    <SubmitButtonForPageWithFutureDueDate
      disabled={isDisabled}
      disabledTooltip={t("page_view.group_mark_done_missing_group")}
      isGrouped={isGrouped}
      page={selectedPage}
      showEditButton={showEditButton}
      toggleDoneStatus={toggleDoneStatus}
    />
  );
};

interface SubmitButtonForStaticPageProps {
  page: PageType;
  showEditButton: boolean;
  toggleDoneStatus: () => void;
}

const SubmitButtonForStaticPage: FC<SubmitButtonForStaticPageProps> = ({
  page,
  showEditButton,
  toggleDoneStatus,
}) => {
  return (
    <PillButton
      eventName={
        showEditButton ? "Student Page - Edit response" : "Student Page - I'm / We're Done"
      }
      eventProps={{ pageTitle: page.title, pageId: page.id }}
      kind={showEditButton ? "dark-gray" : "blue"}
      size="m"
      onClick={toggleDoneStatus}
    >
      <IconText
        iconStart={showEditButton ? <CircleCrossIcon /> : <CheckIcon />}
        text={showEditButton ? t("page_view.mark_incomplete") : t("page_view.mark_complete")}
      />
    </PillButton>
  );
};

interface SubmitButtonForPageWithNoDueDateProps {
  disabled?: boolean;
  disabledTooltip?: ReactNode;
  isGrouped: boolean;
  page: PageType;
  showEditButton: boolean;
  toggleDoneStatus: () => void;
}

const SubmitButtonForPageWithNoDueDate: FC<SubmitButtonForPageWithNoDueDateProps> = ({
  disabled,
  disabledTooltip,
  isGrouped,
  page,
  showEditButton,
  toggleDoneStatus,
}) => {
  const key = showEditButton
    ? isGrouped
      ? "page_view.group_edit_page"
      : "page_view.edit_page"
    : "page_view.done_editing";

  return (
    <Tooltip text={disabled && disabledTooltip}>
      <PillButton
        disabled={disabled}
        eventName={
          showEditButton ? "Student Page - Edit response" : "Student Page - I'm / We're Done"
        }
        eventProps={{ pageTitle: page.title, pageId: page.id }}
        kind={showEditButton ? "dark-gray" : "blue"}
        size="m"
        onClick={toggleDoneStatus}
      >
        <IconText
          iconStart={showEditButton ? <LongFormPencilIcon /> : <CheckIcon />}
          text={t(key)}
        />
      </PillButton>
    </Tooltip>
  );
};

interface SubmitButtonIconProps {
  disabled?: boolean;
  isHovering?: boolean;
}

export const SubmitButtonIcon: FC<SubmitButtonIconProps> = ({ disabled, isHovering }) => {
  return (
    <CircleSuccessIcon
      className={cn({
        "text-green": !disabled && !isHovering,
        "text-black-tint-70": disabled,
        "text-green-tint-20": isHovering,
      })}
    />
  );
};

interface SubmitButtonForPageWithFutureDueDateProps {
  disabled?: boolean;
  disabledTooltip?: ReactNode;
  isGrouped: boolean;
  page: PageType;
  showEditButton: boolean;
  toggleDoneStatus: () => void;
}

const SubmitButtonForPageWithFutureDueDate: FC<SubmitButtonForPageWithFutureDueDateProps> = ({
  disabled,
  disabledTooltip,
  isGrouped,
  page,
  showEditButton,
  toggleDoneStatus,
}) => {
  const sectionUserId = useAppSelector(selectCurrentSectionUserId);
  const pageGroups = useAppSelector((s) => selectPageGroupsWithUsersByPageId(s, page.id)) || [];
  const userPageGroup = pageGroups.find((pageGroup) =>
    pageGroup.page_group_users.find(
      (pageGroupUser) => pageGroupUser.section_user_id === sectionUserId && pageGroupUser.is_ready,
    ),
  );
  const [isHovering, setIsHovering] = useState(false);
  const [hasEditBeenClicked, setHasEditBeenClicked] = useState(false);

  if (!page || !page.due_at) {
    return null;
  }

  const { month, day, time } = monthDayAtTime(page.due_at);
  const haveSubmissionsBeenCollected = Boolean(page.submissions_last_collected_at);
  const isBlueVariant = !haveSubmissionsBeenCollected && hasEditBeenClicked;

  const editButtonTextKey = haveSubmissionsBeenCollected
    ? isGrouped
      ? "page_view.group_edit_page"
      : "page_view.edit_page"
    : isGrouped
      ? "page_view.group_edit_submission"
      : "page_view.edit_submission";

  const submitButtonTextKey = haveSubmissionsBeenCollected
    ? isGrouped
      ? "page_view.group_resubmit"
      : "page_view.individual_resubmit"
    : hasEditBeenClicked
      ? "page_view.done_editing"
      : isGrouped
        ? "page_view.group_submit"
        : "page_view.individual_submit";

  const onEdit = () => {
    setHasEditBeenClicked(true);
    toggleDoneStatus();
  };

  const onSubmit = () => {
    setHasEditBeenClicked(false);
    toggleDoneStatus();
  };

  const onResubmit = () => {
    const options = isGrouped
      ? { page_group_id: userPageGroup?.id }
      : { section_user_id: sectionUserId };
    storeApi.submissions.create({ page_id: page.id, section_id: page.section_id, ...options });

    onSubmit();
  };

  if (showEditButton) {
    return (
      <Tooltip
        text={
          !page.submissions_last_collected_at &&
          t("page_view.response_will_be_submitted", { time, month, day })
        }
      >
        <PillButton
          eventName="Student Page - Edit response"
          eventProps={{ pageTitle: page.title, pageId: page.id }}
          kind="dark-gray"
          size="m"
          onClick={onEdit}
        >
          <IconText iconStart={<LongFormPencilIcon />} text={t(editButtonTextKey)} />
        </PillButton>
      </Tooltip>
    );
  }

  return (
    <Tooltip text={disabled && disabledTooltip}>
      <PillButton
        disabled={disabled}
        eventName="Student Page - I'm / We're Done"
        eventProps={{ pageTitle: page.title, pageId: page.id }}
        kind={isBlueVariant ? "blue" : "green"}
        size="m"
        onClick={() => {
          if (haveSubmissionsBeenCollected) {
            onResubmit();
          } else {
            onSubmit();
          }
        }}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
      >
        <IconText
          iconStart={
            isBlueVariant ? (
              <CheckIcon />
            ) : haveSubmissionsBeenCollected ? (
              <RepeatIcon />
            ) : (
              <SubmitButtonIcon disabled={disabled} isHovering={isHovering} />
            )
          }
          text={t(submitButtonTextKey)}
        />
      </PillButton>
    </Tooltip>
  );
};

export interface SubmitButtonForPageWithPastDueDateProps {
  page: PageType;
  pageAccessData?: PageAccessDataType | null;
  showEditButton: boolean;
  toggleDoneStatus: () => void;
}

export const SubmitButtonForPageWithPastDueDate: FC<SubmitButtonForPageWithPastDueDateProps> = ({
  page,
  pageAccessData,
  showEditButton,
  toggleDoneStatus,
}) => {
  const isWithinLateSubmissionPeriod = isInLateSubmissionWindow(page);

  const { value: isEnabled, setTrue: enable, setFalse: disable } = useBoolean(true);

  const sectionUserId = useAppSelector(selectCurrentSectionUserId);

  const isGrouped = isGroupPage(page);
  const pageGroups = useAppSelector((s) => selectPageGroupsWithUsersByPageId(s, page.id)) || [];
  const userPageGroup = pageGroups.find((pageGroup) =>
    pageGroup.page_group_users.find(
      (pageGroupUser) => pageGroupUser.section_user_id === sectionUserId && pageGroupUser.is_ready,
    ),
  );

  const submissions = useListSelector((s) => selectUserSubmissionsByPageId(s, page.id));
  const hasLateSubmission =
    page.due_at && submissions.some((s) => new Date(s.created_at) > new Date(page.due_at!));

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (!isEnabled) {
      timeout = setTimeout(() => {
        enable();
      }, 5000); // Re-enable Submit Late button after 5 seconds.
    }
    return () => clearTimeout(timeout);
  }, [isEnabled, enable]);

  const options = isGrouped
    ? { page_group_id: userPageGroup?.id }
    : { section_user_id: sectionUserId };

  const onSubmitLate = () => {
    disable();
    storeApi.submissions.create({ page_id: page.id, section_id: page.section_id, ...options });
    if (pageAccessData) {
      storeApi.page_access_data.partial_update({
        id: pageAccessData.id,
        completed_at: new Date().toISOString(),
        completed_by_id: sectionUserId,
      });
    } else {
      serverUpsertPageAccessData({
        page_id: page.id,
        section_id: page.section_id,
        completed_at: new Date().toISOString(),
        completed_by_id: sectionUserId,
        ...options,
      });
    }
  };

  const [isHovering, setIsHovering] = useState(false);

  if (!page || !page.due_at) {
    return null;
  }

  // TODO: use date formatter for this
  const { month, day, time } = monthDayAtTime(
    isLateSubmissionDueDateIndefinite(page)
      ? ""
      : (getLateSubmissionDueDate(page) as Date).toString(),
  );

  const tooltipContent = isWithinLateSubmissionPeriod
    ? t("page_worksheet_header.due_date_has_passed", {
        context: isLateSubmissionDueDateIndefinite(page) ? "indefinite_due_date" : "late_due_date",
        month,
        day,
        time,
      })
    : t("page_worksheet_header.late_due_date");

  if (showEditButton) {
    return (
      <PillButton
        eventName="Student Page - Edit response"
        eventProps={{ pageTitle: page.title, pageId: page.id }}
        kind="dark-gray"
        size="m"
        onClick={toggleDoneStatus}
      >
        <IconText
          iconStart={<LongFormPencilIcon />}
          text={t(isGrouped ? "page_view.group_edit_submission" : "page_view.edit_submission")}
        />
      </PillButton>
    );
  }

  const submitButtonTextKey = hasLateSubmission
    ? isGrouped
      ? "page_view.group_resubmit_late"
      : "page_view.individual_resubmit_late"
    : isGrouped
      ? "page_view.group_submit_late"
      : "page_view.individual_submit_late";

  // TODO: If the late due date passes, this will likely not update the disable state in real time for the student.
  return (
    <Tooltip content={tooltipContent}>
      <PillButton
        disabled={!isEnabled}
        eventName="Student Page - Submit late"
        eventProps={{ pageTitle: page.title, pageId: page.id }}
        kind="green"
        size="m"
        onClick={onSubmitLate}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
      >
        <IconText
          iconStart={
            hasLateSubmission ? (
              <RepeatIcon />
            ) : (
              <SubmitButtonIcon disabled={!isEnabled} isHovering={isHovering} />
            )
          }
          text={t(submitButtonTextKey)}
        />
      </PillButton>
    </Tooltip>
  );
};
